import { ActionTree } from 'vuex'
import { Auth } from './types'
import { RootState } from '../types'
import {
  magentaOAuth,
  connectionService,
  wampUtils,
  ConnectionWSShared
} from '@leapsight/styleguide'
import router from '@/router'
import PersonsHelpers from '@/helpers/persons/personsHelpers'
import { i18n } from '@leapsight/portal'
import store from '@/store'

export const actions: ActionTree<Auth, RootState> = {
  checkTokenIsActive({ commit }) {
    return new Promise((resolve, reject) => {
      //Existe token?
      const token = localStorage.getItem('ACCESS_TOKEN')

      //Valido que este activo
      if (token) {
        magentaOAuth
          .active()
          .then(response => {
            commit('tokenValid', response)
            resolve(response)
          })
          .catch(err => {
            console.log('error ', err)
            reject()
          })
      } else {
        resolve(false)
      }
    })
  },

  async logout({ state, commit }) {
    try {
      const res = await magentaOAuth.logout()
      if (res.status === 200) {
        const connection = state.connection
        if (Boolean(connection)) ConnectionWSShared.closeConnection()
        commit('logout')
        router.push({ name: 'login' }).then(() => {
          window.location.reload()
        })
      }
    } catch (err) {
      console.log(err)
    } finally {
      commit('setDisconnectDialogValue', false, { root: true })
    }
  },

  getConnection({ commit }, { fnConnOnOpen, fnConnOnClose }) {
    // Se debería ejecutar si existe una instancia de la conexión ws
    const conn = ConnectionWSShared.getInstance({ fnConnOnOpen, fnConnOnClose })
      .connection
    commit('setConnection', conn)
  },

  establishConnection({ state, rootState, commit, dispatch }) {
    const fnConnOnOpen = async session => {
      dispatch('wsConnectionActive', true)
      dispatch('setSession', session)
      await dispatch('personAccountFetch')

      dispatch('subscribeUserUpdated', () => {
        wampUtils.TokenServices.clearToken()
        commit(
          'setDisconnectDialogMessage',
          i18n.t('general.disconnectDialogComponent.message_modified_profile'),
          { root: true }
        )
        commit('setDisconnectDialogValue', true, { root: true })
        setTimeout(() => dispatch('logout'), 10000)
      })

      dispatch('subscribeDeviceDeleted', response => {
        if (
          response[0].info.identifier ===
          localStorage.getItem('DEVICE_IDENTIFIER')
        ) {
          wampUtils.TokenServices.clearToken()
          commit(
            'setDisconnectDialogMessage',
            i18n.t('general.disconnectDialogComponent.message_deleted_device'),
            { root: true }
          )
          commit('setDisconnectDialogValue', true, { root: true })
          setTimeout(() => dispatch('logout'), 10000)
        }
      })
    }

    const fnConnOnClose = async (wsReason, wampDetails) => {
      console.error('Reason for disconnection:', wsReason, wampDetails)
      if (wsReason === 'unreachable' || wsReason === 'unsupported') {
        if (!wampDetails.retry_count || wampDetails.retry_count > 10) {
          const snackbar = {
            color: 'warning',
            text: i18n.t(
              'portal.general.user_settings.user_profile.errors.connection_lost_message'
            )
          }
          store.dispatch('ui/setSnackbar', snackbar)
          localStorage.clear()
          window.location.reload()
        }
        return false
      }

      const { reason, details } = wampDetails
      if (wampUtils.isReasonToDisconnect({ reason })) {
        wampUtils.TokenServices.clearToken()
        commit(
          'setDisconnectDialogMessage',
          i18n.t('general.disconnectDialogComponent.message_modified_profile')
        )
        commit('setDisconnectDialogValue', true, { root: true })
        setTimeout(() => {
          commit('logout')
          localStorage.clear()
          router.push({ name: 'login' }).then(() => {
            window.location.reload()
          })
        }, 10000)
      } else {
        commit('things/cleanThingsState', null, { root: true })
        commit('things/resetFilterThingsState', null, { root: true })
        commit('things/resetCurrentThing', null, { root: true })
        try {
          if (
            reason === 'wamp.error.authentication_failed' ||
            reason === 'wamp.close.goodbye_and_out'
          ) {
            let willRetry = true
            for (let i = 0; i < 3 && willRetry; i++) {
              try {
                const res = await magentaOAuth.refreshToken()
                if (res.status === 200) {
                  dispatch('establishConnection')
                  willRetry = false
                } else if (res.status >= 400 && res.status < 500) {
                  commit('logout')
                  localStorage.clear()
                  router.push({ name: 'login' }).then(() => {
                    window.location.reload()
                  })
                  willRetry = false
                } else {
                  willRetry = true
                }
              } catch (err) {
                console.error(err)
                commit('logout')
                localStorage.clear()
                router.push({ name: 'login' }).then(() => {
                  window.location.reload()
                })
                willRetry = false
              }
            }
          }
        } catch (e) {
          console.error(e)
        }
      }
    }

    if (state.connection?.session?.isOpen)
      fnConnOnOpen(state.connection.session).then()
    else {
      // Se debería ejecutar si no existe una instancia de la conexión ws
      dispatch('getConnection', { fnConnOnOpen, fnConnOnClose })
    }
  },
  wsConnectionActive({ commit }, payload) {
    commit('wsConnectionActive', payload)
  },
  setSession({ commit }, payload) {
    commit('setSession', payload)
  },
  async personAccountFetch({ commit }) {
    const userId = connectionService.getBusinessUserId()
    try {
      const data = await PersonsHelpers.personFetch({ userId })
      const userGroups = data.kwargs.security.groups
      const personData = data.args[0]

      let userIsAdmin = false
      let userIsAdminSubFleet = false
      if (userGroups.includes('business_account_admin')) {
        userIsAdmin = true
        userIsAdminSubFleet = false
      } else if (userGroups.includes('business_account_fleet_admin')) {
        userIsAdminSubFleet = true
        userIsAdmin = false
      }

      const user = {
        id: personData.id,
        name: personData.first_name,
        last_name: personData.last_name,
        username: personData.roles['mrn:party_role:user'].username,
        photo: personData.photo,
        isAdmin: userIsAdmin,
        isAdminSubFleet: userIsAdminSubFleet
      }
      commit('setUser', user, { root: true })
      const lsUser = {
        name: user.name,
        last_name: user.last_name
      }
      localStorage.setItem('user', JSON.stringify(lsUser))
    } catch (err) {
      throw new Error(`Error al hacer fetch person - ${err.kwargs.description}`)
    }
  },
  subscribeUserUpdated({ getters }, callback) {
    const connection = getters['connection']
    const accountId = wampUtils.getAccountUUID(connectionService.getAccountId())
    const personId = wampUtils.getPersonUUID(
      connectionService.getBusinessUserId()
    )
    return new Promise<void>(resolve => {
      const topic = `com.magenta.account.${accountId}.user.${personId}.updated`
      connection.session
        .subscribe(topic, callback, { match: 'exact' })
        .then(() => {
          resolve()
        })
    })
  },
  subscribeDeviceDeleted({ getters }, callback) {
    const connection = getters['connection']
    return new Promise<void>(resolve => {
      const topic = 'com.magenta.device.deleted'
      connection.session
        .subscribe(topic, callback, { match: 'exact' })
        .then(() => {
          resolve()
        })
    })
  }
}
